import React, { Component } from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import { slices } from '~/redux/slices';
import { black2, black3, bold, semibold } from '~/assets/shared-styles/general';
import DocumentGroupPage from '~/components/DocumentGroupPage/DocumentGroupPage';
import {
  addDocumentsToGroup,
  deleteDocumentGroup,
  editDocumentGroup,
  getDocumentGroupDetails,
} from '~/components/DocumentGroupsPage/DocumentGroupsPage.data';
import { getDocHandlerPaths } from '~/components/DocumentsViewPage/Document.data';
import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import DocumentTree from '~/components/Shared/DocumentTree/DocumentTree';
import EcFilterInput from '~/components/Shared/EcFilterInput/EcFilterInput';
import EcModal from '~/components/Shared/EcModal/EcModal';
import {
  EcTab,
  EcTabList,
  EcTabPanel,
  EcTabs,
} from '~/components/Shared/EcTabs';
import { showToast } from '~/components/Shared/EcToast/showToast';
import withHover from '~/components/Shared/HOCs/withHover/withHover';
import BackIcon from '~/components/Shared/Icons/BackIcon';
import FolderEmptyIcon from '~/components/Shared/Icons/FolderEmptyIcon';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import { Button } from '~/eds/src/components/Button/Button';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import { canUseDocumentGroupRevamp } from '~/permissions/canUseDocumentGroupRevamp';
import { withRouting } from '~/routing/withRouting';
import {
  MODAL_AUTOCOMPLETE,
  MODAL_DELETE,
  MODAL_DOCUMENT_GROUP_RELATION,
  MODAL_NAME,
} from '~/types/modal.types';
import { ERROR, SUCCESS, WARNING } from '~/types/toast.types';

const BackIconWithHover = withHover(BackIcon);

class Page extends Component {
  constructor(props) {
    super(props);

    this.navigateBack = this.navigateBack.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleOnShowMoreClick = this.handleOnShowMoreClick.bind(this);
    this.onGroupEdit = this.onGroupEdit.bind(this);
    this.onGroupDelete = this.onGroupDelete.bind(this);
    this.handleOnDocumentSearchClick =
      this.handleOnDocumentSearchClick.bind(this);
    this.loadDocumentGroupDetails = this.loadDocumentGroupDetails.bind(this);

    this.state = {
      searchString: '',
      expandedAll: false,
      loading: false,
      errorLoadingMessage: false,
      groupData: {},
      configuredColumns: [],
      selectedItem: null,
      uploadLocation: null,
      uploadFiles: [],
      primaryModal: null,
      secondaryModal: null,
      canModifyFolder: false,
    };
  }

  componentDidMount() {
    this.loadDocumentGroupDetails();
  }

  loadDocumentGroupDetails() {
    const {
      params: { groupId },
    } = this.props;

    this.setState({ groupData: {}, loading: true, errorLoadingMessage: false });
    getDocumentGroupDetails(groupId)
      .then((groupData) => {
        this.setState(
          { groupData, loading: false, errorLoadingMessage: false },
          this.loadHandlerInfo,
        );
      })
      .catch(() =>
        this.setState({ loading: false, errorLoadingMessage: false }),
      );
  }

  loadHandlerInfo() {
    const { groupData } = this.state;
    const { documentTree } = groupData;

    const setDocGroupHandlers = (docGroup) => {
      const { children, handlerIds } = docGroup;
      docGroup.handlers = [];
      getDocHandlerPaths(handlerIds).then(
        ({ data }) => (docGroup.handlers = data),
      );
      if (children.length)
        children.forEach((childDocGroup) => setDocGroupHandlers(childDocGroup));
    };

    for (let relationship in documentTree) {
      const relationshipTree = documentTree[relationship];
      if (!relationshipTree.length) continue;
      relationshipTree.forEach((docGroup) => setDocGroupHandlers(docGroup));
    }
    this.setState({ groupData });
  }

  navigateBack() {
    const { navigate } = this.props;

    navigate(-1);
  }

  handleShowModal(modal, selectedItem = null) {
    this.setState({
      primaryModal: modal,
      selectedItem,
    });
  }

  handleHideModal() {
    this.setState({ primaryModal: null, selectedItem: null });
  }

  onGroupEdit(name, note) {
    const { groupData } = this.state;
    const { intl } = this.props;
    editDocumentGroup(groupData.groupId, { name, note })
      .then((res) => {
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'DocumentGroup.updateSuccess',
              defaultMessage: '{name} has been updated.',
              description:
                'Success message when document group is updated. Example: name = "Contract Group A"',
            },
            { name: res.name },
          ),
        );
        this.loadDocumentGroupDetails();
        this.handleHideModal();
      })
      .catch((err) => {
        if (err.response && err.response.status === 400) {
          showToast(
            ERROR,
            intl.formatMessage({
              id: 'DocumentGroup.duplicateNameError',
              defaultMessage:
                'There is a group somewhere in your organization with the same name. Please choose a different name.',
              description:
                'Error message when trying to create a group with duplicate name',
            }),
          );
        } else {
          showToast(
            WARNING,
            intl.formatMessage({
              id: 'DocumentGroup.updateError',
              defaultMessage:
                'An error occurred while updating a new document group.',
              description:
                'Generic error message when updating document group fails',
            }),
          );
        }
      });
  }

  onGroupDelete() {
    const {
      params: { groupId },
      intl,
    } = this.props;

    deleteDocumentGroup(groupId)
      .then((res) => {
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'DocumentGroup.deleteSuccess',
              defaultMessage: '"{name}" has been deleted.',
              description:
                'Success message when document group is deleted. Example: name = "Contract Group A"',
            },
            { name: res.name },
          ),
        );
        this.handleHideModal();
        this.navigateBack();
      })
      .catch(() => {
        showToast(
          WARNING,
          intl.formatMessage({
            id: 'DocumentGroup.deleteError',
            defaultMessage:
              'An error occurred while deleting the document group.',
            description: 'Error message when deleting document group fails',
          }),
        );
      });
  }

  handleOnDocumentSearchClick(documentId) {
    const { groupData } = this.state;
    const { intl } = this.props;
    const documentIdAsArray = [Number(documentId)];

    addDocumentsToGroup(groupData.groupId, documentIdAsArray)
      .then((data) => {
        this.handleHideModal();
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'DocumentGroup.addDocumentsSuccess',
              defaultMessage:
                'Document(s) added to "{name}" group successfully.',
              description:
                'Success message when documents are added to group. Example: name = "Contract Group A"',
            },
            { name: data.name },
          ),
        );
        this.loadDocumentGroupDetails();
      })
      .catch((err) => {
        this.handleHideModal();
        if (
          err.response &&
          err.response.data &&
          err.response.data.message &&
          err.response.status === 400
        ) {
          showToast(WARNING, `${err.response.data.message}`);
        } else {
          showToast(
            WARNING,
            intl.formatMessage({
              id: 'DocumentGroup.addDocumentsError',
              defaultMessage:
                'An error occurred while adding the document to the group.',
              description: 'Error message when adding documents to group fails',
            }),
          );
        }
      });
  }

  handleOnShowMoreClick(query) {
    const { actions, navigate } = this.props;

    actions.analyzerSetData({
      filters: [{ entity: 'bool_text_search', query }],
    });
    this.handleHideModal();
    navigate('/analyzer/results');
  }

  getMenuItems = () => {
    const { intl } = this.props;
    const items = [];

    items.push(
      {
        content: intl.formatMessage({
          id: 'DocumentGroup.editMenuItem',
          defaultMessage: 'Edit',
          description: 'Edit menu item for document group',
        }),
        onClick: () => this.handleShowModal(MODAL_NAME),
      },
      {
        content: intl.formatMessage({
          id: 'DocumentGroup.deleteMenuItem',
          defaultMessage: 'Delete',
          description: 'Delete menu item for document group',
        }),
        onClick: () => this.handleShowModal(MODAL_DELETE),
      },
    );

    return items;
  };

  renderExtraOptions() {
    const { classes, intl } = this.props;

    return (
      <div className={classes.actionsWrapper}>
        <ActionsMenu
          align="end"
          id="document_tree_page_header_actions_menu"
          items={this.getMenuItems(document)}
          title={intl.formatMessage({
            id: 'DocumentGroup.actionsMenuTitle',
            defaultMessage: 'edit document group menu',
            description: 'Title for document group actions menu',
          })}
        />
      </div>
    );
  }

  renderEmptyDocumentTree() {
    const { classes } = this.props;

    return (
      <div className={classes.emptyPageWrapper}>
        <div className={classes.emptyPageContent}>
          <FolderEmptyIcon />
          <div className={classes.folderEmptyLabel}>
            <FormattedMessage
              id="DocumentGroup.noDocumentsLabel"
              defaultMessage="No documents."
              description="Label shown when document group has no documents"
            />
          </div>
          <span className={classes.folderEmptyInfo}>
            <FormattedMessage
              id="DocumentGroup.noDocumentsInfo"
              defaultMessage="This group does not have any documents yet."
              description="Information text shown when document group is empty"
            />
          </span>
        </div>
      </div>
    );
  }

  renderEditModal() {
    const { groupData } = this.state;
    const { intl } = this.props;

    return (
      <EcModal
        modalType={MODAL_NAME}
        width="560px"
        title={intl.formatMessage({
          id: 'DocumentGroup.editModalTitle',
          defaultMessage: 'Edit Group',
          description: 'Title for edit document group modal',
        })}
        labelText={intl.formatMessage({
          id: 'DocumentGroup.groupLabel',
          defaultMessage: 'Group',
          description: 'Label for group name field',
        })}
        itemsCurrentName={groupData ? groupData.name : ''}
        includeTextArea={true}
        labelTextArea={intl.formatMessage({
          id: 'DocumentGroup.notesLabel',
          defaultMessage: 'NOTES',
          description: 'Label for notes textarea field',
        })}
        itemsCurrentDescription={groupData ? groupData.note : ''}
        confirmButtonText={intl.formatMessage({
          id: 'DocumentGroup.saveButton',
          defaultMessage: 'Save',
          description: 'Save button text in edit modal',
        })}
        handleNameChange={this.onGroupEdit}
        hideModal={this.handleHideModal}
      />
    );
  }

  renderDeleteModal() {
    const { intl } = this.props;
    const modalText = intl.formatMessage(
      {
        id: 'DocumentGroup.deleteConfirmation',
        defaultMessage:
          "Are you sure you want to delete {name}? You won't be able to undo this action.",
        description:
          'Confirmation message for deleting document group. Example: name = "Contract Group A"',
      },
      { name: this.state.groupData.name },
    );

    return (
      <Modal
        children={<Markdown text={modalText} />}
        isVisible
        primaryAction={{
          text: intl.formatMessage({
            id: 'DocumentGroup.deleteButton',
            defaultMessage: 'Delete',
            description: 'Delete button text in confirmation modal',
          }),
          variant: 'danger',
          onClick: () => {
            this.onGroupDelete();
            this.handleHideModal();
          },
        }}
        onHide={this.handleHideModal}
        onCancel={this.handleHideModal}
        title={intl.formatMessage({
          id: 'DocumentGroup.deleteModalTitle',
          defaultMessage: 'Delete Group?',
          description: 'Title for delete confirmation modal',
        })}
      />
    );
  }

  renderDocumentGroupRelationModal() {
    const { selectedItem, groupData } = this.state;
    const { intl } = this.props;
    let documents = [];
    const aggregatedDocuments = (array) => {
      if (!array) return;
      array.forEach(function (c) {
        documents.push({
          title: c.title,
          documentId: c.documentId,
          fileType: c.fileType,
          relationship: c.relationship,
        });
        if (c.children !== undefined) {
          aggregatedDocuments(c.children);
        }
      });
      return documents;
    };

    const groupDocuments = aggregatedDocuments([
      ...groupData.documentTree.linked,
      ...groupData.documentTree.uncategorized,
      ...groupData.documentTree.supporting,
    ]);
    const groupDataForTree = groupDocuments
      ? groupDocuments
          .map(({ title, documentId, fileType, relationship }) => {
            return {
              label: title,
              value: title,
              documentId,
              fileType,
              relationship,
            };
          })
          .filter((doc) => doc.documentId !== selectedItem.node.documentId)
      : null;

    return (
      <EcModal
        modalType={MODAL_DOCUMENT_GROUP_RELATION}
        width="560px"
        title={intl.formatMessage(
          {
            id: 'DocumentGroup.editRelationshipTitle',
            defaultMessage: 'Edit Relationship for {title}',
            description:
              'Title for edit relationship modal. Example: title = "Master Service Agreement"',
          },
          { title: selectedItem.node.title },
        )}
        groupId={groupData.groupId}
        documentGroupCategory={selectedItem.node.relationship}
        groupData={groupDataForTree}
        selectedDocument={selectedItem}
        handleSaveGroupRelation={this.handleSaveGroupRelation}
        hideModal={this.handleHideModal}
        reloadDocumentTreeData={this.loadDocumentGroupDetails}
      />
    );
  }

  renderPageHeader() {
    const { classes, intl } = this.props;
    const { groupData } = this.state;

    return (
      <div className={classes.headerWrapper}>
        <div className={classes.pageHeader}>
          <button
            className={classes.pageBackAction}
            title={intl.formatMessage({
              id: 'DocumentGroup.backButtonTitle',
              defaultMessage: 'Go back to previous page',
              description: 'Title for back button',
            })}
            onClick={this.navigateBack}
          >
            <BackIconWithHover />
          </button>
          <h2 className={classes.pageName}>{groupData.name}</h2>
        </div>
        <div className={classes.documentHeaderActions}>
          <Button
            id="documents_group_tree_header_addDocumentsButton"
            onClick={() => this.handleShowModal(MODAL_AUTOCOMPLETE)}
            text={intl.formatMessage({
              id: 'DocumentGroup.linkDocumentsButton',
              defaultMessage: 'Link New Documents',
              description: 'Button text for linking new documents to group',
            })}
            variant="primary"
          />
          <div className={classes.documentHeaderActionsMenu}>
            {this.renderExtraOptions()}
          </div>
        </div>
      </div>
    );
  }

  renderPageRightPanel() {
    const { classes, formatDate } = this.props;
    const { groupData } = this.state;
    const formattedDateAdded = formatDate(groupData.dateAdded, 'long');

    return (
      <div className={classes.rightPanel}>
        <h2>
          <FormattedMessage
            id="DocumentGroup.groupDetailsTitle"
            defaultMessage="Group Details"
            description="Title for group details panel"
          />
        </h2>
        <div className={classes.rightPanelLabel}>
          <FormattedMessage
            id="DocumentGroup.notesLabel2"
            defaultMessage="NOTES!"
            description="Label for notes section in details panel"
          />
        </div>
        <div className={classes.groupDetailsInfo}>{groupData.note}</div>

        <div className={classes.rightPanelLabel}>
          <FormattedMessage
            id="DocumentGroup.createdByLabel"
            defaultMessage="CREATED BY"
            description="Label for created by field"
          />
        </div>
        <div className={classes.groupDetailsInfo}>{groupData.createdBy}</div>

        <div className={classes.rightPanelLabel}>
          <FormattedMessage
            id="DocumentGroup.createdDateLabel"
            defaultMessage="CREATED DATE"
            description="Label for created date field"
          />
        </div>
        <div className={classes.groupDetailsInfo}>{formattedDateAdded}</div>
      </div>
    );
  }

  renderSearchModal() {
    return (
      <EcModal
        modalType={MODAL_AUTOCOMPLETE}
        width="720px"
        toViewDocument={false}
        forGroupTreeView={true}
        hideModal={this.handleHideModal}
        handleOnSearchItemClick={this.handleOnDocumentSearchClick}
        handleOnShowMoreClick={this.handleOnShowMoreClick}
      />
    );
  }

  render() {
    const { classes, currentUser, intl, ...rest } = this.props;
    const {
      groupData,
      loading,
      errorLoadingMessage,
      primaryModal,
      searchString,
    } = this.state;

    if (canUseDocumentGroupRevamp(currentUser)) {
      // TODO: modernize DocumentGroupPage to reduce need of passing route props (packaged in ...rest).
      return <DocumentGroupPage {...rest} />;
    }

    return (
      <div className={classes.documentsPage}>
        {this.renderPageHeader()}
        {loading && !errorLoadingMessage ? (
          <div className={classes.loadingContainer}>
            <LoadingSpinner size="medium" />
          </div>
        ) : !loading && errorLoadingMessage ? (
          <div className={classes.errorLoadingWrapper}>
            <div className={classes.errorLoadingMessage}>
              <FormattedMessage
                id="DocumentGroup.loadingError"
                defaultMessage="An error occurred when loading the document group information."
                description="Error message when loading document group fails"
              />
            </div>
          </div>
        ) : !loading && !errorLoadingMessage && groupData ? (
          <div className={classes.documentsPageContent}>
            <div className={classes.documentsPageContentTabs}>
              <EcTabs
                className={classes.documentTabs}
                selectedIndex={this.state.tabIndex}
                onSelect={(tabIndex) => this.setState({ tabIndex })}
              >
                <EcTabList className={classes.documentTabList}>
                  <EcTab>
                    <FormattedMessage
                      id="DocumentGroup.documentsTab"
                      defaultMessage="Documents"
                      description="Documents tab label"
                    />
                  </EcTab>
                </EcTabList>
                <EcTabPanel>
                  {groupData.documentTree &&
                  (groupData.documentTree.linked.length ||
                    groupData.documentTree.supporting.length ||
                    groupData.documentTree.uncategorized.length) ? (
                    <div>
                      <div className={classes.pageWrapper}>
                        <div className={classes.pageFilterWrapper}>
                          <EcFilterInput
                            value={searchString}
                            onChange={(searchString) =>
                              this.setState({ searchString })
                            }
                            onClearSearch={() =>
                              this.setState({ searchString: '' })
                            }
                            placeholder={intl.formatMessage({
                              id: 'DocumentGroup.filterPlaceholder',
                              defaultMessage: 'Filter documents...',
                              description:
                                'Placeholder for document filter input',
                            })}
                          />
                        </div>
                      </div>

                      <div className={classes.groupCategory}>
                        <FormattedMessage
                          id="DocumentGroup.uncategorizedCategory"
                          defaultMessage="Uncategorized"
                          description="Label for uncategorized documents category"
                        />
                      </div>
                      {groupData.documentTree.uncategorized.length ? (
                        <div className={classes.groupCategoryContainer}>
                          <DocumentTree
                            allowEdit={true}
                            treeId="uncategorizedTree"
                            groupId={groupData.groupId}
                            masterDocument={groupData.masterDocumentName}
                            treeData={groupData.documentTree.uncategorized}
                            searchString={searchString}
                            handleShowModal={this.handleShowModal}
                            reloadDocumentTreeData={
                              this.loadDocumentGroupDetails
                            }
                          />
                        </div>
                      ) : (
                        <div className={classes.groupCategoryEmptyDocs}>
                          <FormattedMessage
                            id="DocumentGroup.noUncategorizedDocs"
                            defaultMessage="This group does not have any uncategorized documents."
                            description="Message shown when no uncategorized documents exist"
                          />
                        </div>
                      )}

                      <div className={classes.groupCategory}>
                        <FormattedMessage
                          id="DocumentGroup.documentTreeCategory"
                          defaultMessage="Document Tree"
                          description="Label for document tree category"
                        />
                      </div>
                      {groupData.documentTree.linked.length ? (
                        <div className={classes.groupCategoryContainer}>
                          <DocumentTree
                            allowEdit={true}
                            treeId="linkedTree"
                            groupId={groupData.groupId}
                            masterDocument={groupData.masterDocumentName}
                            treeData={groupData.documentTree.linked}
                            searchString={searchString}
                            handleShowModal={this.handleShowModal}
                            reloadDocumentTreeData={
                              this.loadDocumentGroupDetails
                            }
                          />
                        </div>
                      ) : (
                        <div className={classes.groupCategoryEmptyDocs}>
                          <FormattedMessage
                            id="DocumentGroup.noDocumentTree"
                            defaultMessage="This group does not have a document tree."
                            description="Message shown when no document tree exists"
                          />
                        </div>
                      )}

                      <div className={classes.groupCategory}>
                        <FormattedMessage
                          id="DocumentGroup.supportingDocsCategory"
                          defaultMessage="Supporting Documents"
                          description="Label for supporting documents category"
                        />
                      </div>
                      {groupData.documentTree.supporting.length ? (
                        <div className={classes.groupCategoryContainer}>
                          <DocumentTree
                            allowEdit={true}
                            treeId="supportingTree"
                            groupId={groupData.groupId}
                            masterDocument={groupData.masterDocumentName}
                            treeData={groupData.documentTree.supporting}
                            searchString={searchString}
                            handleShowModal={this.handleShowModal}
                            reloadDocumentTreeData={
                              this.loadDocumentGroupDetails
                            }
                          />
                        </div>
                      ) : (
                        <div className={classes.groupCategoryEmptyDocs}>
                          <FormattedMessage
                            id="DocumentGroup.noSupportingDocs"
                            defaultMessage="This group does not have any supporting documents."
                            description="Message shown when no supporting documents exist"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>{this.renderEmptyDocumentTree()}</div>
                  )}
                </EcTabPanel>
              </EcTabs>
            </div>
            {this.renderPageRightPanel()}
          </div>
        ) : null}
        {primaryModal === MODAL_AUTOCOMPLETE && this.renderSearchModal()}
        {primaryModal === MODAL_NAME && this.renderEditModal()}
        {primaryModal === MODAL_DELETE && this.renderDeleteModal()}
        {primaryModal === MODAL_DOCUMENT_GROUP_RELATION &&
          this.renderDocumentGroupRelationModal()}
      </div>
    );
  }
}

const mapStateToProps = ({ currentUser }) => ({ currentUser });

const mapDispatchToProps = (dispatch) => ({
  actions: {
    analyzerSetData: (data) => dispatch(slices.analyzer.actions.setData(data)),
  },
});

const styles = {
  documentsPage: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  loadingContainer: {
    marginTop: '64px',
    textAlign: 'center',
  },
  emptyPageWrapper: {
    display: 'flex',
    flexGrow: '1',
    alignItems: 'center',
    justifyContent: 'center',
    height: '70%',
  },
  emptyPageContent: {
    textAlign: 'center',
  },
  folderEmptyLabel: {
    margin: '72px 0 8px 0',
    color: black2,
    fontSize: '17px',
    fontWeight: semibold,
    textAlign: 'center',
  },
  headerWrapper: {
    marginBottom: '21px',
    display: 'flex',
    alignItems: 'center',
  },
  pageName: {
    margin: '0 0 0 8px',
    color: black2,
    fontSize: '32px',
    fontWeight: bold,
  },
  pageHeader: {
    marginBottom: '21px',
    display: 'inline-flex',
    alignItems: 'center',
    flexGrow: 1,
  },
  pageBackAction: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  documentHeaderActions: {
    display: 'flex',
    alignItems: 'center',
  },
  documentHeaderActionsMenu: {
    marginLeft: '20px',
  },
  documentsPageContent: {
    display: 'flex',
  },
  documentsPageContentTabs: {
    flexGrow: 1,
  },
  rightPanel: {
    margin: '23px 0 0 30px',
    width: '320px',
    fontSize: '14px',
  },
  rightPanelLabel: {
    fontSize: '12px',
    fontWeight: semibold,
  },
  groupDetailsInfo: {
    margin: '10px 0 20px',
  },
  pageWrapper: {
    height: '100%',
    width: '100%',
    margin: 0,
    display: 'flex',
  },
  pageFilterWrapper: {
    display: 'flex',
    flexGrow: 1,
  },
  groupCategory: {
    marginTop: '30px',
    color: black2,
    fontSize: '21px',
    fontWeight: bold,
  },
  groupCategoryNoMasterDocs: {
    marginTop: '20px',
    padding: '10px',
    width: '94%',
    border: '1px dashed #ffc449',
    backgroundColor: '#fffdf6',
  },
  groupCategoryEmptyDocs: {
    marginTop: '20px',
    color: black3,
  },
};

Page = withRouting(Page);

Page = injectIntl(connect(mapStateToProps, mapDispatchToProps)(Page));

export default injectSheet(styles)(withFormatDate(Page));
